import Layout from "../components/Layout";
import ScrollReveal from "../components/ScrollReveal";
import GayatriSlideshow from "../components/GayatriSlideshow";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Home, Zap, Shield, Award, Users } from "lucide-react";

export default function HomePage() {
  const services = [
    { icon: Home, title: "Residential Buildings", description: "Premium residential complexes, apartments and housing societies built to last." },
    { icon: Hammer, title: "Renovations", description: "Transform existing spaces with our comprehensive renovation services." },
    { icon: Zap, title: "Infrastructure", description: "Build robust infrastructure projects that stand for generations." },
  ];

  const features = [
    { icon: Award, title: "Award-Winning", description: "Recognized for excellence and quality in construction." },
    { icon: Users, title: "Expert Team", description: "Experienced professionals dedicated to your project's success." },
    { icon: Shield, title: "Safety First", description: "Highest standards of safety on every project site." },
  ];

  const projects = [
    { title: "Poornima Heights", category: "Ongoing", image: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/xrlh1jcr_Cam%2003%20PURNIMA_EXT_NK_005_Opt%2001%20copy.jpg", slug: "poornima-heights" },
    { title: "Gayatri Darshan", category: "Ongoing", image: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/ifcvkn1f_gayatri_new2.jpeg", slug: "gayatri-darshan" },
    { title: "Vamantas", category: "Upcoming", image: "https://customer-assets.emergentagent.com/job_feature-branch-55/artifacts/wkuekyhu_Gemini_Generated_Image_ewsxdxewsxdxewsx.png", slug: "vamantas" },
  ];

  return (
    <Layout>
      {/* Upcoming Projects Slideshow */}
      <GayatriSlideshow />

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Our Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Comprehensive construction solutions tailored to meet your unique needs and vision.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="p-8 border border-border rounded-xl hover:shadow-lg transition-shadow group">
                    <div className="w-16 h-16 bg-secondary/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-secondary/30 transition-colors">
                      <Icon size={32} className="text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-3">{service.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{service.description}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Featured Projects</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Showcase of our finest construction achievements.</p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {projects.map((project, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <Link to={`/projects/${project.slug}`} className="group rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow block">
                  <div className="h-64 overflow-hidden bg-gray-200 relative">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6 bg-white">
                    <span className="inline-block px-3 py-1 bg-secondary/20 text-secondary font-semibold text-sm rounded-full mb-3">{project.category}</span>
                    <h3 className="text-xl font-bold text-primary">{project.title}</h3>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="text-center">
              <Link to="/projects" data-testid="view-all-projects-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white font-semibold rounded-lg hover:opacity-90 transition-opacity">
                View All Projects <ArrowRight size={18} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Why Choose Arrow</h2>
                <p className="text-lg text-muted-foreground">Years of excellence and commitment to quality construction.</p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <ScrollReveal key={index} delay={index * 0.1}>
                    <div className="text-center">
                      <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon size={40} className="text-secondary" />
                      </div>
                      <h3 className="text-xl font-bold text-primary mb-3">{feature.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-20 md:py-24">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Build Your Vision?</h2>
              <p className="text-xl text-gray-200 mb-8">Contact us today for a free consultation and let's discuss your next project.</p>
              <Link to="/contact" data-testid="cta-quote-btn" className="inline-flex items-center gap-2 px-8 py-4 bg-secondary text-secondary-foreground font-bold rounded-lg hover:opacity-90 transition-opacity text-lg">
                Get our best quote <ArrowRight size={20} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
}
